import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PlatformUserAccountService } from '@data/_generated-api/platform-api/services/platform-user-account.service';
import { UpamErrorMapper } from '@data/_generated-api/error-mappers/upam-error-mapper';
import { AsyncResult } from '@common/result/asyncResult';
import { ok } from '@common/result/Result';
import { PlatformUserAccountDto } from '@data/_generated-api/platform-api/models/platform-user-account-dto';
import { PlatformRegistrationKeyDto } from '@data/_generated-api/platform-api/models/platform-registration-key-dto';
import { PlatformApiResultOfUserAccountDto } from '@data/_generated-api/platform-api/models/platform-api-result-of-user-account-dto';

/**
 * Admin-acties voor accountbeheer: accountlijst, account-acties,
 * registratie-toggle en registratiecodes. Vereist het EditUsers-feature.
 */
@Injectable({ providedIn: 'root' })
export class AccountAdminApiService {
  private readonly api = inject(PlatformUserAccountService);
  private readonly errorMapper = inject(UpamErrorMapper);

  async getAllAccounts(): AsyncResult<PlatformUserAccountDto[]> {
    const result = await firstValueFrom(this.api.postUseraccountsQueryDatasource({ body: {} }));
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value?.items ?? []);
  }

  async deactivate(userAccountId: string): AsyncResult<PlatformUserAccountDto> {
    return this.toResult(
      await firstValueFrom(this.api.postUseraccountsActionDeactivate({ body: { userAccountId } }))
    );
  }

  async reactivate(userAccountId: string): AsyncResult<PlatformUserAccountDto> {
    return this.toResult(
      await firstValueFrom(this.api.postUseraccountsActionReactivate({ body: { userAccountId } }))
    );
  }

  async adminVerify(userAccountId: string): AsyncResult<PlatformUserAccountDto> {
    return this.toResult(
      await firstValueFrom(this.api.postUseraccountsActionAdminVerify({ body: { userAccountId } }))
    );
  }

  async triggerPasswordReset(userAccountId: string, language: string): AsyncResult<PlatformUserAccountDto> {
    return this.toResult(
      await firstValueFrom(
        this.api.postUseraccountsActionAdminTriggerPasswordReset({ body: { userAccountId, language } })
      )
    );
  }

  async forceLogout(userAccountId: string): AsyncResult<PlatformUserAccountDto> {
    return this.toResult(
      await firstValueFrom(this.api.postUseraccountsActionForceLogout({ body: { userAccountId } }))
    );
  }

  private async toResult(result: PlatformApiResultOfUserAccountDto): AsyncResult<PlatformUserAccountDto> {
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value);
  }

  async isRegistrationEnabled(): AsyncResult<boolean> {
    const result = await firstValueFrom(this.api.getUseraccountsRegistrationConfig());
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value?.registrationEnabled === true);
  }

  async setRegistrationEnabled(enabled: boolean): AsyncResult<boolean> {
    const result = await firstValueFrom(
      this.api.postUseraccountsActionSetRegistrationEnabled({ body: { enabled } })
    );
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value?.registrationEnabled === true);
  }

  async getRegistrationKeys(): AsyncResult<PlatformRegistrationKeyDto[]> {
    const result = await firstValueFrom(this.api.getUseraccountsRegistrationKeys());
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok([...(result.value ?? [])]);
  }

  async createRegistrationKey(userGroupId: string, expiresOn: string | null): AsyncResult<PlatformRegistrationKeyDto> {
    const result = await firstValueFrom(
      this.api.postUseraccountsActionCreateRegistrationKey({ body: { userGroupId, expiresOn } })
    );
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value);
  }

  async revokeRegistrationKey(registrationKeyId: string): AsyncResult<PlatformRegistrationKeyDto> {
    const result = await firstValueFrom(
      this.api.postUseraccountsActionRevokeRegistrationKey({ body: { registrationKeyId } })
    );
    if (!result.isValid) {
      return this.errorMapper.mapInvalidResult(result.errors);
    }
    return ok(result.value);
  }

}
